import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getReservationDiscount } from '../api/discountsApi'
import { useAuth } from '@/features/auth/AuthContext'

export default function DiscountAllocationManager({ reservationId, projectId, unitPrice, discountData: passedDiscount }) {
  const { profile } = useAuth()
  const { data: fetchedDiscountData, isLoading } = useQuery({
    queryKey: ['reservation-discount', reservationId],
    queryFn: () => getReservationDiscount(reservationId),
    enabled: !passedDiscount,
  })

  const discount = passedDiscount || fetchedDiscountData?.data
  const currentAllocation = discount?.discount_allocations?.[0] || discount?.allocation?.[0]

  if (isLoading && !passedDiscount) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="spinner spinner-md text-primary-600"></div>
      </div>
    )
  }

  if (!discount || !currentAllocation) {
    return null
  }

  const totalDiscount = parseFloat(discount.locked_dollar_amount)
  const depositContribution = parseFloat(currentAllocation.deposit_contribution || 0)
  const priceReduction = parseFloat(currentAllocation.price_reduction || 0)
  const unallocated = parseFloat(currentAllocation.unallocated || 0)

  const discountPercent = discount.unit_price_at_lock
    ? ((totalDiscount / parseFloat(discount.unit_price_at_lock)) * 100).toFixed(1)
    : null

  const hasDepositContribution = depositContribution > 0

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <span className="font-semibold text-green-900">
          {discountPercent}% Discount Locked
        </span>
        <span className="ml-auto text-lg font-bold text-green-700">
          ${totalDiscount.toLocaleString('en-NZ')}
        </span>
      </div>

      <div className="space-y-2">
        {hasDepositContribution && (
          <div className="flex justify-between items-center text-sm bg-white/60 rounded px-3 py-2">
            <span className="text-green-700">Deposit contribution</span>
            <span className="font-semibold text-green-900">
              ${depositContribution.toLocaleString('en-NZ')}
            </span>
          </div>
        )}

        <div className="flex justify-between items-center text-sm bg-white/60 rounded px-3 py-2">
          <span className="text-green-700">Price reduction</span>
          <span className="font-semibold text-green-900">
            ${(priceReduction + unallocated).toLocaleString('en-NZ')}
          </span>
        </div>
      </div>

      {hasDepositContribution && (
        <p className="text-xs text-green-700 mt-3 pt-3 border-t border-green-200">
          Your deposit contribution helps with your home loan approval.
        </p>
      )}

      {!hasDepositContribution && profile?.buyer_category === 'first_home' && (
        <div className="mt-3 pt-3 border-t border-green-200">
          <p className="text-xs text-green-800 font-medium mb-1">Recommendation for First Home Buyers</p>
          <p className="text-xs text-green-700">
            Consider allocating part of your discount towards your deposit. This can help with your home loan approval and reduce the cash you need upfront.
          </p>
        </div>
      )}

      {profile?.buyer_intent === 'investment' && (
        <div className="mt-3 pt-3 border-t border-green-200">
          <p className="text-xs text-green-800 font-medium mb-1">Recommendation for Investors</p>
          <p className="text-xs text-green-700">
            Applying your full discount to price reduction maximizes your rental yield and improves your investment returns.
          </p>
        </div>
      )}
    </div>
  )
}
